import { z } from 'zod'

export const MeetingStateSchema = z.enum(['scheduled', 'active', 'ended', 'cancelled'])
export type MeetingState = z.infer<typeof MeetingStateSchema>

// observer — сотрудник видит встречу по политике прозрачности, сам не организатор
// и не участник (все сотрудники видят все встречи). customer — заказчик в своей
// витрине: бэкенд отдаёт эту роль всем встречам customer-выдачи.
export const MeetingRoleSchema = z.enum(['organizer', 'participant', 'observer', 'customer'])
export type MeetingRole = z.infer<typeof MeetingRoleSchema>

// customer — заказчик, вошедший по своей сессии хаба (не по гостевой ссылке).
export const ParticipantKindSchema = z.enum(['employee', 'guest', 'customer'])
export type ParticipantKind = z.infer<typeof ParticipantKindSchema>

export const ParticipantStateSchema = z.enum(['waiting', 'joined', 'left'])
export type ParticipantState = z.infer<typeof ParticipantStateSchema>

export const TranscriptStatusSchema = z.enum(['none', 'queued', 'ready', 'failed'])
export type TranscriptStatus = z.infer<typeof TranscriptStatusSchema>

// Кто на встрече глазами списка: активная отвечает «кто в комнате сейчас»,
// завершённая — «кто был», запланированная — «кого позвали». names — верхушка
// состава (организатор первым), total — весь состав, из него считается «ещё N».
export const RosterKindSchema = z.enum(['in_call', 'attended', 'invited'])
export type RosterKind = z.infer<typeof RosterKindSchema>

export const RosterSchema = z.object({
  kind: RosterKindSchema,
  total: z.number().int().nonnegative(),
  names: z.array(z.string()).default([]),
})
export type Roster = z.infer<typeof RosterSchema>

export const MeetingSummarySchema = z.object({
  id: z.string(),
  title: z.string(),
  state: MeetingStateSchema,
  role: MeetingRoleSchema,
  tags: z.array(z.string()).default([]),
  // Привязка звонка к компании/проекту хаба (hub-migration): пусто = внутренняя
  // встреча. Имена приезжают из проекции хаба вместе со списком — резолвить id
  // отдельным запросом не нужно; пусто, если запись ещё не досинкана.
  companyId: z.string().optional(),
  projectId: z.string().optional(),
  companyName: z.string().optional(),
  projectName: z.string().optional(),
  participantCount: z.number().int().nonnegative(),
  // Состав строки: имена и полный размер. Карточка живёт на нём, а не на
  // participantCount — у завершённой встречи это разные числа.
  roster: RosterSchema.default({ kind: 'invited', total: 0, names: [] }),
  // Закрытый звонок: вход через лобби даже для сотрудника — «войти» в списке
  // означает «постучаться», а не «оказаться в комнате».
  locked: z.boolean().default(false),
  // Приватная встреча видна только своему кругу; в списке нужна меткой, чтобы
  // автор понимал, что остальные её не видят.
  private: z.boolean().default(false),
  // Встреча открыта клиенту компании: он видит её, запись и расшифровку.
  // Ортогонально private — приватную клиенту открыть нельзя (бэк вернёт 422).
  customerVisible: z.boolean().default(false),
  scheduledAt: z.string().optional(),
  // Фактическое начало; у встреч, созданных до появления поля, отсутствует —
  // потребители падают на createdAt.
  startedAt: z.string().optional(),
  endedAt: z.string().optional(),
  createdAt: z.string(),
  // Источники совпадения поиска q (title|description|tags|transcript);
  // пусто вне поисковой выдачи — бейджи не рисуем.
  matchSources: z.array(z.string()).default([]),
})
export type MeetingSummary = z.infer<typeof MeetingSummarySchema>

export const DetailParticipantSchema = z.object({
  participantId: z.string(),
  kind: ParticipantKindSchema,
  name: z.string(),
  username: z.string().optional(),
  state: ParticipantStateSchema,
  // Отделы сотрудника из проекции хаба; у гостя пусто (не hub-принципал).
  departments: z.array(z.string()).default([]),
})
export type DetailParticipant = z.infer<typeof DetailParticipantSchema>

export const LobbyParticipantSchema = z.object({
  participantId: z.string(),
  kind: ParticipantKindSchema,
  name: z.string(),
  state: ParticipantStateSchema,
})
export type LobbyParticipant = z.infer<typeof LobbyParticipantSchema>

// Позванный заранее сотрудник; joined=false — «позвали, но в звонок не заходил».
export const InviteeSchema = z.object({
  subject: z.string(),
  name: z.string().optional(),
  username: z.string().optional(),
  invitedBy: z.string().optional(),
  joined: z.boolean().default(false),
})
export type Invitee = z.infer<typeof InviteeSchema>

export const MeetingDetailSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  tags: z.array(z.string()).default([]),
  // Привязка звонка к компании/проекту хаба (hub-migration): пусто = внутренняя.
  // Имена — из проекции хаба вместе с деталью.
  companyId: z.string().optional(),
  projectId: z.string().optional(),
  companyName: z.string().optional(),
  projectName: z.string().optional(),
  state: MeetingStateSchema,
  // Права организатора — явный флаг; guestLink больше не маркер (его видят все сотрудники).
  isOrganizer: z.boolean().default(false),
  // Замок звонка; щёлкнуть может любой участник-сотрудник.
  locked: z.boolean().default(false),
  // Приватность; переключить может любой из круга встречи.
  private: z.boolean().default(false),
  // Открыта клиенту компании + аудит пометки: кто открыл и когда. Имя пометившего
  // резолвит бэк; без него в подсказке остаётся идентификатор.
  customerVisible: z.boolean().default(false),
  customerVisibleBy: z.string().optional(),
  customerVisibleByName: z.string().optional(),
  customerVisibleAt: z.string().optional(),
  // Вьюер в круге встречи (или админ): вправе управлять составом и видимостью.
  // Публичную встречу видят все, но круг её — не общий.
  canManageAccess: z.boolean().default(false),
  organizer: z.object({ name: z.string(), subject: z.string() }),
  participants: z.array(DetailParticipantSchema),
  invitees: z.array(InviteeSchema).default([]),
  recording: z.object({ exists: z.boolean(), state: z.string().optional() }),
  transcriptStatus: TranscriptStatusSchema,
  // Прогресс подготовки транскрипта, % — актуален при transcriptStatus=queued.
  transcriptProgress: z.number().int().min(0).max(100).default(0),
  guestLink: z.string().optional(),
  // Право удалить встречу целиком считает бэк (admin + не-active) — фронт
  // только показывает/прячет действие.
  canDelete: z.boolean(),
  scheduledAt: z.string().optional(),
  // Фактическое начало; у встреч, созданных до появления поля, отсутствует —
  // потребители падают на createdAt.
  startedAt: z.string().optional(),
  endedAt: z.string().optional(),
  createdAt: z.string(),
})
export type MeetingDetail = z.infer<typeof MeetingDetailSchema>

export const CreatedMeetingSchema = z.object({
  id: z.string(),
  title: z.string(),
  state: MeetingStateSchema,
  guestLink: z.string(),
  companyId: z.string().optional(),
  projectId: z.string().optional(),
  // Итоговая пометка «видно заказчику»: бэк подтверждает, что просьба формы
  // прошла инварианты агрегата.
  customerVisible: z.boolean().default(false),
  scheduledAt: z.string().optional(),
})
export type CreatedMeeting = z.infer<typeof CreatedMeetingSchema>

export const CreateMeetingInputSchema = z.object({
  title: z.string().min(1, 'Введите название').max(200),
  description: z.string().max(2000, 'Слишком длинное описание').optional(),
  // Компания-клиент или тема; пусто — внутренняя встреча. Лимиты бэка: 10 × 50.
  tags: z
    .array(z.string().min(1).max(50, 'Слишком длинный тег'))
    .max(10, 'Не больше 10 тегов')
    .optional(),
  // Привязка к хабу: company_id опционален; project требует company (иначе бэк
  // вернёт 422 PROJECT_REQUIRES_COMPANY) — дублируем инвариант на клиенте.
  companyId: z.string().max(36).optional(),
  projectId: z.string().max(36).optional(),
  scheduledAt: z.string().optional(),
  // Спрятать встречу сразу при создании.
  private: z.boolean().optional(),
  // Открыть клиенту компании сразу при создании; требует companyId и
  // несовместимо с private (инвариант бэка, зеркалим в форме).
  customerVisible: z.boolean().optional(),
  // Кого позвать сразу — subject'ы сотрудников хаба; имя резолвит бэк.
  invitees: z.array(z.string()).max(50, 'Не больше 50 приглашённых').optional(),
})
export type CreateMeetingInput = z.infer<typeof CreateMeetingInputSchema>

// project без company невозможен — общий предикат для форм create/edit.
export function projectRequiresCompany(companyId?: string, projectId?: string): boolean {
  return !!projectId && !companyId
}

// Почему встречу нельзя открыть клиенту. Инварианты держит бэк (422), но UI
// обязан объяснить их до отказа: упереться в ошибку, чтобы узнать правило, —
// не объяснение.
export type CustomerVisibilityBlock = 'private' | 'no-company'

export function customerVisibilityBlock(input: {
  private?: boolean | undefined
  companyId?: string | undefined
}): CustomerVisibilityBlock | undefined {
  if (input.private) return 'private'
  if (!input.companyId) return 'no-company'
  return undefined
}

// Строка автокомплита тегов: имя + сколько встреч им помечено.
export const TagSuggestionSchema = z.object({
  name: z.string(),
  usageCount: z.number().int().nonnegative(),
})
export type TagSuggestion = z.infer<typeof TagSuggestionSchema>
